import { USER_ROLE, APPROVER_TAG, AUTO_APPROVE, isL1AutoApproved } from '../../../../utils/constants';
import { activeUserIdsFromUserRoles, normalizeUserIdList } from '../../../Setup/TaskList/utils/assignmentRoles';

const L1_APPROVE_ACTIONS = ['approved_task'];
const L2_SIGNOFF_ACTIONS = ['l2_approved_task', 'signoff', 'signed_off_task'];

function normalizeIds(raw) {
  if (!raw) return [];
  const list = Array.isArray(raw) ? raw : [raw];
  return list.filter(Boolean).map(String);
}

function resolveUser(id, userMap = {}) {
  const name = userMap?.[id]?.name || String(id);
  return {
    id,
    name,
    initial: name.charAt(0).toUpperCase(),
  };
}

function actionName(action) {
  if (typeof action === 'string') return action;
  return action?.action || '';
}

function actionTime(action) {
  const at = action?.acted_at;
  const time = at ? new Date(at).getTime() : 0;
  return Number.isNaN(time) ? 0 : time;
}

function findActorIdByActions(userRoles, actionTypes, { role, tag } = {}) {
  if (!Array.isArray(userRoles)) return null;
  let match = null;
  let matchAt = -1;
  userRoles.forEach((roleEntry) => {
    if (roleEntry?.status === false) return;
    if (role && roleEntry.role !== role) return;
    if (tag != null && roleEntry.tag !== tag) return;
    const actions = Array.isArray(roleEntry.actions_performed) ? roleEntry.actions_performed : [];
    actions.forEach((action) => {
      if (!actionTypes.includes(actionName(action))) return;
      const at = actionTime(action);
      if (!match || at >= matchAt) {
        match = roleEntry;
        matchAt = at;
      }
    });
  });
  if (!match) return null;
  return match.user_id || match.user?._id || null;
}

function findL2ActorId(userRoles) {
  const approverActor = findActorIdByActions(userRoles, L2_SIGNOFF_ACTIONS, {
    role: USER_ROLE.APPROVER,
    tag: APPROVER_TAG.L2,
  });
  if (approverActor) return approverActor;
  return findActorIdByActions(userRoles, L2_SIGNOFF_ACTIONS, {
    role: USER_ROLE.MANAGER,
    tag: APPROVER_TAG.L2,
  });
}

function resolveL1Ids(task) {
  const fromRoles = activeUserIdsFromUserRoles(task?.user_roles, USER_ROLE.APPROVER, APPROVER_TAG.L1);
  if (Array.isArray(task?.user_roles) && task.user_roles.length) return fromRoles;
  return normalizeIds(task?.approver);
}

function resolveL2Ids(task) {
  const fromRoles = activeUserIdsFromUserRoles(task?.user_roles, USER_ROLE.APPROVER, APPROVER_TAG.L2);
  if (Array.isArray(task?.user_roles) && task.user_roles.length) return fromRoles;
  return normalizeUserIdList(task?.sign_off_approvers);
}

function resolveRequiresSignOff(task, l2Ids) {
  const approvalState = task?.approval_state || {};
  if (task?.requires_sign_off != null) return !!task.requires_sign_off;
  if (approvalState.l2_required != null) return !!approvalState.l2_required;
  return l2Ids.length > 0;
}

function buildGroup(ids, completed, actorId, userMap, completedTooltipPrefix) {
  const users = ids.map((id) => resolveUser(id, userMap));
  const actor = actorId
    ? resolveUser(actorId, userMap)
    : (completed && users.length === 1 ? users[0] : null);

  return {
    users,
    completed,
    completedBy: actor,
    completedTooltip: actor && completedTooltipPrefix
      ? `${completedTooltipPrefix} ${actor.name}`
      : null,
  };
}

/**
 * Build display model for the task list Approver column (L1 approvers + optional L2 sign-off).
 */
export function buildTaskApproverCellModel(task, userMap = {}) {
  const approvalState = task?.approval_state || {};
  const l1Ids = resolveL1Ids(task);
  const l2Ids = resolveL2Ids(task);
  const requiresSignOff = resolveRequiresSignOff(task, l2Ids);

  const l1AutoApproved = isL1AutoApproved(task);
  const l1Completed = !!approvalState.l1_completed || l1AutoApproved;
  const l2Completed = !!approvalState.l2_completed;
  const l1ActorId = l1Completed && !l1AutoApproved
    ? findActorIdByActions(task?.user_roles, L1_APPROVE_ACTIONS, {
      role: USER_ROLE.APPROVER,
      tag: APPROVER_TAG.L1,
    })
    : null;
  const l2ActorId = l2Completed ? findL2ActorId(task?.user_roles) : null;

  const l1 = buildGroup(l1Ids, l1Completed, l1ActorId, userMap, 'Approved By');
  if (l1AutoApproved) {
    l1.systemActor = true;
    l1.completed = true;
    l1.completedTooltip = AUTO_APPROVE.DETAIL_BADGE;
  }

  return {
    requiresSignOff,
    l1AutoApproved,
    l1,
    l2: requiresSignOff
      ? buildGroup(l2Ids, l2Completed, l2ActorId, userMap, 'Signed Off By')
      : null,
    hasApprovers: l1Ids.length > 0 || (requiresSignOff && l2Ids.length > 0) || l1AutoApproved,
  };
}
